import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import {
  ShieldCheck, AlertTriangle, CheckCircle, XCircle,
  ArrowLeft, FileText, Globe, Clock, Zap, Loader2, Lock, Sparkles
} from 'lucide-react';
import axios from 'axios';
import AgencyAuditTools from '../components/AgencyAuditTools';
import { findExplanation } from '../data/auditExplanations';

const API_URL = 'https://cyberguard-api-ka8f.onrender.com';

// ✅ Grade DPE visuel
const DPE_CONFIG = {
  A: { color: '#22c55e', bg: 'bg-green-500',  label: 'Excellent',   width: '100%' },
  B: { color: '#84cc16', bg: 'bg-lime-500',   label: 'Très bon',    width: '85%'  },
  C: { color: '#eab308', bg: 'bg-yellow-500', label: 'Bon',         width: '70%'  },
  D: { color: '#f97316', bg: 'bg-orange-500', label: 'Moyen',       width: '55%'  },
  E: { color: '#ef4444', bg: 'bg-red-500',    label: 'Insuffisant', width: '40%'  },
  F: { color: '#dc2626', bg: 'bg-red-600',    label: 'Faible',      width: '25%'  },
  G: { color: '#991b1b', bg: 'bg-red-800',    label: 'Critique',    width: '10%'  },
};


export default function AuditDetail() {
  const { id } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [expandedLines, setExpandedLines] = useState({});

  const plan = user?.subscription_plan || 'free';
  const canGenerateReport = plan === 'pro' || plan === 'enterprise';

  useEffect(() => { fetchAudit(); }, [id, token]);

  const fetchAudit = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/audits/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAudit(res.data);
    } catch (err) {
      console.error('Erreur chargement audit:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    try {
      const res = await axios.post(`${API_URL}/api/audits/${id}/report`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate(`/reports/${res.data.report_id}`);
    } catch (err) {
      console.error('Erreur génération rapport:', err);
    } finally {
      setGeneratingReport(false);
    }
  };

  const toggleLine = (i) => setExpandedLines(prev => ({ ...prev, [i]: !prev[i] }));

  const getLineIcon = (line) => {
    if (line.startsWith('✅')) return <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />;
    if (line.startsWith('❌')) return <XCircle className="w-4 h-4 text-red-500 shrink-0" />;
    if (line.startsWith('⚠️')) return <AlertTriangle className="w-4 h-4 text-yellow-500 shrink-0" />;
    return <span className="w-4 h-4 shrink-0" />;
  };

  const getLineColor = (line) => {
    if (line.startsWith('✅')) return 'text-zinc-300';
    if (line.startsWith('❌')) return 'text-red-300';
    if (line.startsWith('⚠️')) return 'text-yellow-300';
    if (line.startsWith('──')) return 'text-zinc-600 text-[10px] uppercase tracking-widest';
    return 'text-zinc-500';
  };

  // Séparer les sections
  const getSections = (details) => {
    const sections = { security: [], dns: [], rgpd: [], deep: [] };
    let current = 'security';
    for (const line of (details || [])) {
      if (line.includes('Analyse DNS')) { current = 'dns'; continue; }
      if (line.includes('Audit RGPD')) { current = 'rgpd'; continue; }
      if (line.startsWith('── Analyse approfondie')) { current = 'deep'; continue; }
      if (line.startsWith('🔒 Analyse approfondie')) { sections.deep.push(line); continue; }
      sections[current].push(line);
    }
    return sections;
  };

  if (loading) return (
    <DashboardLayout>
      <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-4">
        <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-zinc-500 font-mono text-xs uppercase tracking-widest animate-pulse">Chargement des résultats...</p>
      </div>
    </DashboardLayout>
  );

  if (!audit) return (
    <DashboardLayout>
      <div className="text-center py-20">
        <p className="text-zinc-500 font-mono text-sm">Audit introuvable.</p>
        <Button onClick={() => navigate('/dashboard')} className="mt-4 bg-blue-600 font-mono rounded-none text-xs">← Retour</Button>
      </div>
    </DashboardLayout>
  );

  const dpe = DPE_CONFIG[audit.dpe_grade] || DPE_CONFIG['D'];
  const sections = getSections(audit.scan_details);

  const renderLines = (lines) => lines.map((line, i) => {
    const explanation = findExplanation(line);
    const isExpanded = expandedLines[`${line}${i}`];
    const cleanText = line.replace('✅ ', '').replace('❌ ', '').replace('⚠️ ', '').replace('ℹ️ ', '');

    return (
      <div key={i} className={`rounded-sm transition-all ${isExpanded ? 'bg-zinc-800/50' : 'hover:bg-zinc-800/20'}`}>
        <div
          className={`flex items-start gap-3 p-3 ${explanation ? 'cursor-pointer' : ''}`}
          onClick={() => explanation && toggleLine(`${line}${i}`)}
        >
          {getLineIcon(line)}
          <span className={`flex-1 font-mono text-xs leading-relaxed ${getLineColor(line)}`}>{cleanText}</span>
          {explanation && (
            <span className={`text-[10px] font-mono shrink-0 transition-colors ${isExpanded ? 'text-blue-400' : 'text-zinc-600'}`}>ℹ</span>
          )}
        </div>
        {explanation && isExpanded && (
          <div className="mx-3 mb-3 p-3 bg-blue-500/5 border border-blue-500/15 rounded-sm">
            <p className="text-blue-300 text-xs font-sans leading-relaxed">
              <span className="mr-2">{explanation.emoji}</span>
              <span className="font-semibold text-blue-200">En clair : </span>
              {explanation.simple}
            </p>
          </div>
        )}
      </div>
    );
  });

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6">

        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-zinc-500 hover:text-white font-mono text-xs uppercase transition-colors">
          <ArrowLeft className="w-4 h-4" /> Retour au dashboard
        </button>

        {/* Header avec score + DPE */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="space-y-2">
              <h1 className="text-xl font-black font-mono uppercase text-white tracking-tight">{audit.name}</h1>
              <div className="flex flex-wrap items-center gap-4 text-zinc-500 font-mono text-xs">
                <span className="flex items-center gap-1"><Globe className="w-3 h-3" /> {audit.target_domain}</span>
                <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(audit.created_at).toLocaleDateString('fr-FR')}</span>
                <span className="flex items-center gap-1"><Zap className="w-3 h-3" /> Plan {audit.plan_used}</span>
              </div>
            </div>

            {/* Score + DPE côte à côte */}
            <div className="flex items-center gap-6 shrink-0">
              {/* Score numérique */}
              <div className="text-center">
                <div className="text-5xl font-black font-mono" style={{ color: dpe.color }}>
                  {audit.security_score}
                </div>
                <div className="text-zinc-500 font-mono text-[10px] uppercase">/100</div>
              </div>

              {/* Grade DPE */}
              <div className="text-center">
                <div className="relative w-16 h-16 flex items-center justify-center rounded-sm border-2" style={{ borderColor: dpe.color, backgroundColor: dpe.color + '20' }}>
                  <span className="text-3xl font-black font-mono" style={{ color: dpe.color }}>{audit.dpe_grade || 'D'}</span>
                </div>
                <div className="text-zinc-500 font-mono text-[10px] uppercase mt-1">{dpe.label}</div>
              </div>
            </div>
          </div>

          {/* Barre DPE visuelle */}
          <div className="mt-4 pt-4 border-t border-zinc-800">
            <div className="flex items-center gap-1">
              {Object.entries(DPE_CONFIG).map(([grade, config]) => (
                <div
                  key={grade}
                  className={`flex-1 h-6 flex items-center justify-center font-mono font-black text-xs text-white transition-all ${audit.dpe_grade === grade ? 'scale-110 shadow-lg' : 'opacity-40'}`}
                  style={{ backgroundColor: config.color }}
                >
                  {grade}
                </div>
              ))}
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-zinc-600 font-mono text-[9px]">Excellent</span>
              <span className="text-zinc-600 font-mono text-[9px]">Critique</span>
            </div>
          </div>
        </div>

        {/* Compteurs vulnérabilités */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Critiques', count: audit.vulnerabilities?.high || 0, color: 'text-red-400', border: 'border-red-500/20', bg: 'bg-red-500/5' },
            { label: 'Moyennes', count: audit.vulnerabilities?.medium || 0, color: 'text-yellow-400', border: 'border-yellow-500/20', bg: 'bg-yellow-500/5' },
            { label: 'Faibles', count: audit.vulnerabilities?.low || 0, color: 'text-blue-400', border: 'border-blue-500/20', bg: 'bg-blue-500/5' },
          ].map(({ label, count, color, border, bg }) => (
            <div key={label} className={`${bg} border ${border} rounded-sm p-4 text-center`}>
              <div className={`text-3xl font-black font-mono ${color}`}>{count}</div>
              <div className="text-zinc-500 font-mono text-[10px] uppercase tracking-widest mt-1">{label}</div>
            </div>
          ))}
        </div>

        {/* Résultats Sécurité */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
          <div className="px-6 py-4 border-b border-zinc-800 bg-black/20 flex items-center justify-between">
            <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-blue-500" /> Sécurité Web & DNS
            </h2>
            <span className="text-zinc-600 font-mono text-[10px]">Cliquez sur ℹ pour l'explication</span>
          </div>
          <div className="p-4 space-y-1">
            {sections.security.length > 0 && renderLines(sections.security)}
            {sections.dns.length > 0 && (
              <>
                <div className="pt-3 pb-1 px-2">
                  <span className="text-zinc-600 font-mono text-[10px] uppercase tracking-widest">── Analyse DNS ──</span>
                </div>
                {renderLines(sections.dns)}
              </>
            )}
          </div>
        </div>

        {/* Analyse approfondie */}
        {sections.deep.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm" data-testid="deep-scan">
            <div className="px-6 py-4 border-b border-zinc-800 bg-black/20">
              <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                {audit.deep_scan ? <Zap className="w-4 h-4 text-green-500" /> : <Lock className="w-4 h-4 text-zinc-500" />} Analyse approfondie
              </h2>
            </div>
            <div className="p-4 space-y-1">
              {renderLines(sections.deep)}
            </div>
            {!audit.deep_scan && (
              <div className="px-4 pb-4">
                <button onClick={() => navigate('/domains')} className="text-blue-400 hover:text-blue-300 font-mono text-[10px] uppercase tracking-widest">
                  Vérifier ce domaine →
                </button>
              </div>
            )}
          </div>
        )}

        {/* Audit RGPD */}
        {sections.rgpd.length > 0 && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
            <div className="px-6 py-4 border-b border-zinc-800 bg-black/20">
              <h2 className="text-white font-mono font-bold text-xs uppercase tracking-widest flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-500" /> Audit RGPD & Conformité Légale
              </h2>
            </div>
            <div className="p-4 space-y-1">
              {renderLines(sections.rgpd)}
            </div>
            <div className="px-4 pb-4">
              <p className="text-zinc-600 font-mono text-[10px] leading-relaxed">
                ℹ️ Cet audit vérifie les éléments légaux visibles publiquement sur votre site. Il ne remplace pas un audit juridique complet.
              </p>
            </div>
          </div>
        )}

        {plan === 'enterprise' && <AgencyAuditTools audit={audit} onAuditChange={setAudit} />}

        {/* Bouton Rapport IA */}
        {canGenerateReport ? (
          <div className="bg-zinc-900 border border-blue-500/20 rounded-sm p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-500/10 border border-blue-500/20 flex items-center justify-center rounded-sm shrink-0">
                <Sparkles className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-white font-mono font-bold text-sm">Rapport IA + Guide de correction</p>
                <p className="text-zinc-500 font-mono text-xs mt-1">Analyse détaillée, recommandations priorisées et instructions de correction pas à pas.</p>
              </div>
            </div>
            <Button onClick={handleGenerateReport} disabled={generatingReport} className="bg-blue-600 hover:bg-blue-700 font-mono rounded-none text-xs uppercase shrink-0">
              {generatingReport ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Génération...</> : <><Sparkles className="w-3 h-3 mr-2" /> Générer_Rapport_IA</>}
            </Button>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-zinc-800 flex items-center justify-center rounded-sm shrink-0">
                <Lock className="w-5 h-5 text-zinc-600" />
              </div>
              <div>
                <p className="text-zinc-300 font-mono font-bold text-sm">
                  {(() => {
                    const v = audit?.vulnerabilities || {};
                    const n = (v.high || 0) + (v.medium || 0) + (v.low || 0);
                    return n > 0
                      ? `${n} point${n > 1 ? 's' : ''} à corriger — comment faire ?`
                      : 'Rapport IA et guide de correction';
                  })()}
                </p>
                <p className="text-zinc-500 font-mono text-xs mt-1">
                  Le plan Pro vous donne le rapport IA en langage simple et, pour chaque faille, les instructions de correction à transmettre à votre prestataire.
                </p>
              </div>
            </div>
            <Button onClick={() => navigate('/billing')} className="bg-blue-600 hover:bg-blue-700 font-mono rounded-none text-xs uppercase shrink-0">
              Débloquer les corrections →
            </Button>
          </div>
        )}

        <div className="text-center">
          <button onClick={() => navigate('/reports')} className="text-zinc-500 hover:text-white font-mono text-xs uppercase transition-colors underline">
            Voir tous mes rapports →
          </button>
        </div>

      </div>
    </DashboardLayout>
  );
}
